import { resolveSemanticColor } from '@/theme/semanticTokens';
import { fontFamilies, s } from '@/theme/tokens';
import { Box, Panel, Stack, Text } from '@/ui';
import { settingsPanelSurfaceSx } from '@/ui/composites/settingsPageTypographySx';
import { ErrorOutline, HelpOutline } from '@/ui/primitives';
import { BlockOutlined, CheckCircleOutlineIcon } from '@/ui/primitives/icons';
import { ValidationMissingValuesSection } from './ValidationMissingValuesSection';
import type { ValidationPreviewData } from './validationDataChecks';
import type { SxProps, Theme } from '@/ui/primitives/types';
import type { ReactElement } from 'react';

type ProjectCsvValidationResultPanelProps = {
  hasValidatedFile: boolean;
  previewData: ValidationPreviewData;
  fileName?: string | null;
  container?: 'panel' | 'embedded';
  actions?: ReactElement | null;
};

type ValidationResultState = {
  label: string;
  tone: 'success' | 'error' | 'warning' | 'textSecondary';
  icon: ReactElement;
};

const requiredHeaderLabels = new Set(['Test', 'Status']);

const resolveValidationResultState = (
  hasValidatedFile: boolean,
  previewData: ValidationPreviewData,
): ValidationResultState => {
  if (!hasValidatedFile) {
    return {
      label: 'Unvalidated',
      tone: 'textSecondary',
      icon: <HelpOutline fontSize="small" sx={{ fontSize: 18 }} />,
    };
  }

  const hasRequiredMissingHeaders = previewData.missingHeaderLabels.some((label) =>
    requiredHeaderLabels.has(label),
  );
  const hasInvalidValues =
    previewData.missingTestValues.length > 0 ||
    previewData.missingSuiteValues.length > 0 ||
    previewData.invalidStatusValues.length > 0 ||
    previewData.invalidDurationValues.length > 0 ||
    previewData.invalidTimestampValues.length > 0;

  if (hasRequiredMissingHeaders || hasInvalidValues) {
    return {
      label: 'Invalid',
      tone: 'error',
      icon: <ErrorOutline fontSize="small" sx={{ fontSize: 18 }} />,
    };
  }

  if (previewData.missingHeaderLabels.length > 0) {
    return {
      label: 'Valid with missing optional headers',
      tone: 'warning',
      icon: <BlockOutlined fontSize="small" sx={{ fontSize: 18 }} />,
    };
  }

  if (previewData.missingStatusLabels.length > 0) {
    return {
      label: 'Valid with undetected statuses',
      tone: 'warning',
      icon: <HelpOutline fontSize="small" sx={{ fontSize: 18 }} />,
    };
  }

  return {
    label: 'Valid',
    tone: 'success',
    icon: <CheckCircleOutlineIcon fontSize="small" sx={{ fontSize: 18 }} />,
  };
};

export const ProjectCsvValidationResultPanel = ({
  hasValidatedFile,
  previewData,
  fileName,
  container = 'panel',
  actions = null,
}: ProjectCsvValidationResultPanelProps): ReactElement => {
  const validationResultState = resolveValidationResultState(hasValidatedFile, previewData);
  const hasValidationDetails =
    previewData.invalidDurationValues.length > 0 ||
    previewData.invalidTimestampValues.length > 0 ||
    previewData.invalidStatusValues.length > 0 ||
    previewData.missingTestValues.length > 0 ||
    previewData.missingSuiteValues.length > 0 ||
    previewData.missingHeaderLabels.length > 0 ||
    previewData.missingStatusLabels.length > 0;
  const validationResultTitleSx = {
    fontSize: (theme: Theme) => theme.typography.subtitle1.fontSize ?? 'inherit',
    lineHeight: (theme: Theme) => theme.typography.subtitle1.lineHeight ?? 'normal',
  } satisfies SxProps<Theme>;

  const content = (
    <Stack gap="sm">
      <Box
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: s('xs'),
          minHeight: '32px',
          fontWeight: theme.typography.subtitle1.fontWeight,
        })}
      >
        {fileName ? (
          <>
            <Text component="span" role="body" sx={validationResultTitleSx}>
              Validation Result
            </Text>
            <Text
              component="span"
              role="body"
              color="textSecondary"
              sx={[validationResultTitleSx, { ml: s('xxs') }]}
            >
              ·
            </Text>
            <Text
              component="span"
              role="body"
              sx={[
                validationResultTitleSx,
                {
                  fontFamily: fontFamilies.mono,
                  color: 'text.secondary',
                },
              ]}
            >
              {fileName}
            </Text>
          </>
        ) : (
          <Text component="span" role="body" sx={validationResultTitleSx}>
            Validation Result
          </Text>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: s('md'),
          minHeight: '40px',
          width: '100%',
        }}
      >
        <Text
          role="body"
          color="textSecondary"
          sx={(theme) => ({
            display: 'inline-flex',
            alignItems: 'center',
            gap: s('sm'),
            fontSize: theme.typography.subtitle1.fontSize,
            minWidth: 0,
          })}
        >
          <Text
            component="span"
            role="body"
            sx={(theme) => ({
              display: 'inline-flex',
              color: resolveSemanticColor(theme, validationResultState.tone),
            })}
          >
            {validationResultState.icon}
          </Text>
          {validationResultState.label}
        </Text>
        {actions}
      </Box>
      {hasValidatedFile && hasValidationDetails ? (
        <Stack gap="md">
          <ValidationMissingValuesSection
            title="Invalid Durations"
            values={previewData.invalidDurationValues}
          />
          <ValidationMissingValuesSection
            title="Invalid Statuses"
            values={previewData.invalidStatusValues}
          />
          <ValidationMissingValuesSection
            title="Invalid Timestamps"
            values={previewData.invalidTimestampValues}
          />
          <ValidationMissingValuesSection
            title="Missing Test Values"
            values={previewData.missingTestValues}
          />
          <ValidationMissingValuesSection
            title="Missing Suite Values"
            values={previewData.missingSuiteValues}
          />
          <ValidationMissingValuesSection
            title="Missing Headers"
            values={previewData.missingHeaderLabels}
          />
          <ValidationMissingValuesSection
            title="Undetected Statuses"
            values={previewData.missingStatusLabels}
          />
        </Stack>
      ) : null}
    </Stack>
  );

  if (container === 'embedded') {
    return <Box>{content}</Box>;
  }

  return (
    <Panel
      title="Validation Result"
      footerGap="xs"
      surfaceVariant="settings"
      sx={settingsPanelSurfaceSx}
    >
      {content}
    </Panel>
  );
};
